/**
 * Entry point loaded by Webflow. Deliberately tiny: it only decides *whether* to run
 * the 3D hero, then pulls the heavy bundle in with a dynamic import so nothing
 * competes with the hero H1 for LCP.
 *
 *   <script type="module" src="https://CDN_HOST/mmw-hero.js"></script>
 *
 * Assets are resolved relative to this module by default; override with
 * `data-base="https://…/"` on the container.
 *
 * Debug flags (query string): `mmwDebug=1`, `mmwModel=0..7`, `mmwReduced=1`,
 * `mmwNoWebgl=1`.
 */

const CONTAINER_ID = 'hero-morph';

function hasWebGL2() {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2'));
  } catch {
    return false;
  }
}

/** Very low-end / metered connections get the poster instead of a WebGL context. */
function prefersLightweight() {
  const connection = navigator.connection;
  if (connection?.saveData) return true;
  if (typeof navigator.deviceMemory === 'number' && navigator.deviceMemory < 4) return true;
  return false;
}

function showPoster(container) {
  const src = container.dataset.poster;
  if (!src) return; // Purely decorative — an empty box is an acceptable fallback.
  const image = document.createElement('img');
  image.src = src;
  image.alt = '';
  image.loading = 'lazy';
  image.decoding = 'async';
  image.style.cssText = 'display:block;width:100%;height:auto;max-height:100%;object-fit:contain';
  container.appendChild(image);
  container.dataset.mmwFallback = 'poster';
}

function whenIdle() {
  return new Promise((resolve) => {
    const run = () =>
      'requestIdleCallback' in window
        ? requestIdleCallback(() => resolve(), { timeout: 2000 })
        : setTimeout(resolve, 200);
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  });
}

async function boot() {
  const container = document.getElementById(CONTAINER_ID);
  if (!container) return;

  const params = new URLSearchParams(window.location.search);
  const debug = params.get('mmwDebug') === '1';

  if (params.get('mmwNoWebgl') === '1' || !hasWebGL2()) {
    showPoster(container);
    return;
  }
  if (prefersLightweight()) {
    showPoster(container);
    return;
  }

  const staticFrame =
    params.get('mmwReduced') === '1' ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const modelParam = params.get('mmwModel');
  const forceModel = modelParam !== null && Number.isFinite(Number(modelParam))
    ? Number(modelParam)
    : null;

  const baseUrl = container.dataset.base
    ? new URL(container.dataset.base, window.location.href)
    : new URL('.', import.meta.url);

  await whenIdle();

  try {
    const { mount } = await import('./scene.js');
    await mount({ container, baseUrl, staticFrame, forceModel, debug });
  } catch (error) {
    console.error('[mmw-hero] failed to start, falling back to poster', error);
    showPoster(container);
  }
}

boot();
